import { BaseElement } from 'base';

class FaqBlock extends BaseElement {
  #frame = 0;

  onConnect() {
    this.qa('[data-faq-row]').forEach(row => {
      this.listen(row, 'click', this.#toggle);
      this.listen(row, 'pointerenter', this.#onEnter);
      this.listen(row, 'pointerleave', this.#onLeave);
    });

    this.cleanup(() => {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
      this.#sync();
    });

    this.#bindControl();
  }

  #toggle(event) {
    const row = event.currentTarget;
    this.#setOpen(row, row.getAttribute('aria-expanded') !== 'true');
  }

  #setOpen(row, open) {
    row.setAttribute('aria-expanded', String(open));
    const answer = this.q(`#${CSS.escape(row.getAttribute('aria-controls'))}`);
    if (answer) answer.inert = !open;
    this.#scheduleSync();
  }

  #scheduleSync() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#sync();
    });
  }

  #sync() {
    this.qa('[data-faq-row]').forEach(row =>
      row
        .closest('[data-faq-item]')
        ?.toggleAttribute(
          'data-open',
          row.getAttribute('aria-expanded') === 'true'
        )
    );
  }

  #closeAll() {
    this.qa('[data-faq-row][aria-expanded="true"]').forEach(row =>
      this.#setOpen(row, false)
    );
  }

  async #bindControl() {
    const control = this.q('segmented-control');
    if (!control) return;

    await customElements.whenDefined('segmented-control');
    if (!this.isConnected) return;

    control.run(({ value }) => this.#showPanel(value));
    this.#showPanel(control.value);
  }

  #showPanel(value) {
    this.#closeAll();
    this.qa('[data-faq-row][data-tile]').forEach(row =>
      row.removeAttribute('data-tile')
    );
    this.qa('[data-faq-panel]').forEach(panel => {
      panel.hidden = panel.dataset.faqPanel !== value;
    });
  }

  #onEnter(event) {
    if (event.pointerType !== 'mouse') return;

    const row = event.currentTarget;
    row.toggleAttribute('data-tile-jump', true);
    row.dataset.tile = this.#edge(row, event);
    row.getBoundingClientRect();
    row.removeAttribute('data-tile-jump');
    row.dataset.tile = 'in';
  }

  #onLeave(event) {
    if (event.pointerType !== 'mouse') return;

    const row = event.currentTarget;
    row.dataset.tile = this.#edge(row, event);
  }

  #edge(row, event) {
    const { top, height } = row.getBoundingClientRect();
    return event.clientY < top + height / 2 ? 'top' : 'bottom';
  }
}

if (!customElements.get('faq-block'))
  customElements.define('faq-block', FaqBlock);
